import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Switch } from "./ui/switch";
import { Plug, PlugZap, Wifi, WifiOff, Mail, ShieldAlert, Clock, Zap, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { money } from "../lib/format";

const ENVS = [
  { k: "demo", label: "DEMO" },
  { k: "live", label: "LIVE" },
];

export const LiveControls = ({ config, tv, emailEnabled, traded, sessions }) => {
  const [busy, setBusy] = useState(false);
  const connected = tv?.connected;
  const isMicro = config?.contract_mode === "MICRO";
  const openCount = Object.values(sessions || {}).filter((s) => s?.open).length;
  const totalSessions = Object.keys(sessions || {}).length;

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r && r.ok === false) toast.error(r.error || "Failed", { className: "font-mono text-xs" });
      else if (okMsg) toast.success(okMsg, { className: "font-mono text-xs" });
      return r;
    } catch {
      toast.error("Request failed", { className: "font-mono text-xs" });
    } finally {
      setBusy(false);
    }
  };

  const testConn = async () => {
    const r = await run(api.tvTest);
    if (r?.ok) toast.success(`Auth OK · ${r.account?.name || "account"} (${r.env})`, { className: "font-mono text-xs" });
    else if (r) toast.error(`Auth failed: ${r.error}`, { className: "font-mono text-xs" });
  };

  // Live-only terminal: always operate in LIVE once the broker is connected (no PAPER toggle).
  useEffect(() => {
    if (connected && config && config.mode !== "LIVE") {
      api.setMode("LIVE").catch(() => {});
    }
  }, [connected, config?.mode]);

  const toggleTrust = (v) => {
    if (v) {
      if (!isMicro) return toast.error("Trust Mode requires MICRO contract size", { className: "font-mono text-xs" });
      if (!window.confirm("Enable TRUST MODE? Qualifying MICRO live setups will auto-execute REAL orders WITHOUT a confirmation card. Only enable once you trust the strategy."))
        return;
      run(() => api.trustMode(true), "Trust Mode ON — micro trades auto-execute");
    } else {
      run(() => api.trustMode(false), "Trust Mode OFF");
    }
  };

  return (
    <div className="border-t border-zinc-800">
      <div className="px-3 py-2 flex items-center justify-between">
        <span className="text-[9px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
          <ShieldAlert size={11} className="text-amber-400" /> Live Trading
        </span>
        <span className={`flex items-center gap-1 text-[10px] font-semibold ${connected ? "text-green-500" : "text-zinc-500"}`}>
          {connected ? <Wifi size={11} /> : <WifiOff size={11} />}
          {connected ? (tv?.authorized ? "AUTHORIZED" : "CONNECTED") : "OFFLINE"}
        </span>
      </div>

      {/* environment selector */}
      <div className="px-3 pb-2 flex gap-1">
        {ENVS.map((e) => (
          <button
            key={e.k}
            data-testid={`tv-env-${e.k}`}
            disabled={connected || busy}
            onClick={() => run(() => api.tvEnv(e.k), `Endpoint: ${e.label}`)}
            className={`flex-1 px-1 py-1 text-[9px] font-semibold rounded-sm border transition-colors duration-150 disabled:opacity-40
              ${config?.env === e.k ? "bg-blue-500/15 text-blue-400 border-blue-500/40" : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:bg-zinc-800"}`}
          >
            {e.label}
          </button>
        ))}
      </div>

      <div className="px-3 pb-2 grid grid-cols-2 gap-1">
        <button
          data-testid="tv-test-btn"
          disabled={busy}
          onClick={testConn}
          className="px-2 py-1.5 text-[10px] font-semibold rounded-sm bg-zinc-800 text-zinc-200 hover:bg-zinc-700 transition-colors duration-150 disabled:opacity-40"
        >
          Test Auth
        </button>
        {connected ? (
          <button
            data-testid="tv-disconnect-btn"
            disabled={busy}
            onClick={() => run(api.tvDisconnect, "Disconnected")}
            className="flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-semibold rounded-sm bg-red-600/80 text-white hover:bg-red-600 transition-colors duration-150 disabled:opacity-40"
          >
            <PlugZap size={12} /> Disconnect
          </button>
        ) : (
          <button
            data-testid="tv-connect-btn"
            disabled={busy}
            onClick={() => run(api.tvConnect, "Connecting…")}
            className="flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-semibold rounded-sm bg-blue-600 text-white hover:bg-blue-500 transition-colors duration-150 disabled:opacity-40"
          >
            <Plug size={12} /> Connect
          </button>
        )}
      </div>

      {tv?.last_error && !connected && (
        <div className="px-3 pb-2 text-[9.5px] text-red-400/80 font-mono break-words">{tv.last_error}</div>
      )}
      {connected && (
        <div className="px-3 pb-2 grid grid-cols-2 gap-x-2 text-[10px] font-mono">
          <span className="text-zinc-500">Account</span>
          <span className="text-right text-zinc-200">{tv.account_spec || tv.account_id || "—"}</span>
          <span className="text-zinc-500">Live Cash</span>
          <span className="text-right text-green-400">{tv.cash != null ? money(tv.cash) : "—"}</span>
        </div>
      )}
      {connected && tv?.md_denied && (
        <div data-testid="md-denied-banner" className="mx-3 mb-2 rounded-sm border border-amber-500/40 bg-amber-500/10 px-2 py-1.5">
          <div className="text-[10px] font-bold text-amber-400 mb-0.5">Live index data not enabled over the API</div>
          <p className="text-[9.5px] leading-snug text-amber-100/80">
            Connected (live balance shown above), but Tradovate's market-data API returned:
            "{tv.md_error || 'Symbol is inaccessible'}". Your CME platform bundle covers the Tradovate
            app itself — streaming quotes over the API needs <b>API-permissioned</b> market data.
            Contact Tradovate support and ask them to enable <b>real-time CME market data over the API
            (API Access)</b> for this account. Until then, indices run on delayed fallback data
            (crypto is unaffected — it uses a separate live feed).
          </p>
        </div>
      )}

      {/* contract size */}
      <div className="px-3 py-2 border-t border-zinc-800/70">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] uppercase tracking-wide text-zinc-400">Contract Size</span>
          <span className="text-[9px] text-zinc-500 font-mono">
            {Object.values(traded || {}).map((t) => t.root).join(" · ")}
          </span>
        </div>
        <div className="flex gap-1">
          {["MICRO", "FULL"].map((mo) => (
            <button
              key={mo}
              data-testid={`contract-${mo.toLowerCase()}`}
              disabled={busy}
              onClick={() => run(() => api.contractMode(mo), `Contract size: ${mo}`)}
              className={`flex-1 px-2 py-1 text-[10px] font-bold rounded-sm border transition-colors duration-150 disabled:opacity-40
                ${config?.contract_mode === mo ? "bg-blue-500/15 text-blue-400 border-blue-500/40" : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:bg-zinc-800"}`}
            >
              {mo}
            </button>
          ))}
        </div>
        <p className="mt-1 text-[9px] text-zinc-500 leading-snug">
          {config?.contract_mode === "MICRO"
            ? "Micro futures (1/10 size) — fits a small balance."
            : "Full-size E-mini — needs larger margin (~$12k+)."}
        </p>
      </div>

      {/* session filter */}
      <div className="px-3 py-2 border-t border-zinc-800/70">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-zinc-400 flex items-center gap-1">
            <Clock size={11} className="text-blue-400" /> Session filter
            <span className={`ml-1 px-1 py-0.5 rounded-sm text-[8.5px] font-bold ${openCount > 0 ? "bg-green-500/15 text-green-400" : "bg-zinc-700/40 text-zinc-500"}`}>
              {openCount}/{totalSessions} OPEN
            </span>
          </span>
          <Switch
            data-testid="session-filter-toggle"
            checked={!!config?.session_filter}
            onCheckedChange={(v) => run(() => api.sessionFilter(v), v ? "Session filter ON" : "Session filter OFF")}
            className="data-[state=checked]:bg-blue-500"
          />
        </div>
        <div className="mt-1.5 grid grid-cols-2 gap-x-2 gap-y-0.5 text-[9px] font-mono">
          {Object.entries(sessions || {}).map(([sym, s]) => (
            <div key={sym} data-testid={`session-${sym}`} className="flex items-center justify-between">
              <span className="text-zinc-500">{traded?.[sym]?.root || sym}</span>
              <span className={s?.open ? "text-green-400" : "text-zinc-600"}>
                {s?.open ? s.label : "CLOSED"}
              </span>
            </div>
          ))}
        </div>
        <p className="mt-1 text-[9px] text-zinc-500 leading-snug">
          Indices hunt only during US RTH (09:30–16:00 ET); crypto/metals run 24/5.
        </p>
      </div>

      {/* trust mode */}
      <div className="px-3 py-2 border-t border-zinc-800/70">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-zinc-400 flex items-center gap-1">
            <Zap size={11} className={config?.trust_mode ? "text-green-400" : "text-zinc-500"} /> Trust Mode
            <span className={`ml-1 px-1 py-0.5 rounded-sm text-[8.5px] font-bold ${config?.trust_mode ? "bg-green-500/15 text-green-400" : "bg-zinc-700/40 text-zinc-500"}`}>
              {config?.trust_mode ? "AUTO" : "MANUAL"}
            </span>
          </span>
          <Switch
            data-testid="trust-mode-toggle"
            checked={!!config?.trust_mode}
            disabled={!isMicro && !config?.trust_mode}
            onCheckedChange={toggleTrust}
            className="data-[state=checked]:bg-green-500"
          />
        </div>
        <p className="mt-1 text-[9px] leading-snug text-zinc-500">
          {config?.trust_mode
            ? "MICRO live setups auto-execute WITHOUT a confirmation card."
            : isMicro
              ? "Skip the Confirm card and auto-execute MICRO live trades."
              : "Switch Contract Size to MICRO to enable auto-execute."}
        </p>
      </div>

      {/* persist live arming across restarts */}
      <div className="px-3 py-2 border-t border-zinc-800/70">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-zinc-400 flex items-center gap-1">
            <RefreshCw size={11} className={config?.persist_arming ? "text-amber-400" : "text-zinc-500"} /> Persist Arming
            <span className={`ml-1 px-1 py-0.5 rounded-sm text-[8.5px] font-bold ${config?.persist_arming ? "bg-amber-500/15 text-amber-400" : "bg-zinc-700/40 text-zinc-500"}`}>
              {config?.persist_arming ? "ON" : "OFF"}
            </span>
          </span>
          <Switch
            data-testid="persist-arming-toggle"
            checked={!!config?.persist_arming}
            onCheckedChange={(v) => run(() => api.persistArming(v), v ? "Persist arming ON — LIVE + Trust survive restarts" : "Persist arming OFF")}
            className="data-[state=checked]:bg-amber-500"
          />
        </div>
        <p className="mt-1 text-[9px] leading-snug text-zinc-500">
          {config?.persist_arming
            ? "After a restart/deploy the agent auto-resumes LIVE + Trust Mode once reconnected."
            : "Restarts return to PAPER with Trust OFF (safe default). Turn on to keep LIVE armed."}
        </p>
      </div>

      {/* email alerts */}
      <div className="px-3 py-2 border-t border-zinc-800/70 flex items-center justify-between">
        <span className="text-[10px] text-zinc-400 flex items-center gap-1">
          <Mail size={11} /> Risk email alerts
          <span className={`ml-1 ${emailEnabled ? "text-green-500" : "text-zinc-600"}`}>
            {emailEnabled ? "ON" : "not configured"}
          </span>
        </span>
        <button
          data-testid="test-email-btn"
          onClick={async () => {
            const r = await run(api.testEmail);
            if (r && !r.sent) toast.message(r.reason || "Email not configured", { className: "font-mono text-xs" });
            else if (r?.sent) toast.success("Test email sent", { className: "font-mono text-xs" });
          }}
          className="px-2 py-1 text-[9px] font-semibold rounded-sm bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors duration-150"
        >
          Test
        </button>
      </div>
    </div>
  );
};
